import { Component, EventEmitter, Input, OnInit, OnChanges, Output, SimpleChanges } from "@angular/core";
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { CreateUserDto } from "@merchant_api/models/create-user-dto";
import { RoleDto } from "@merchant_api/models/role-dto";
import { UserDetailsDto } from "@merchant_api/models/user-details-dto";
import { ActionButtonKind, ValidatorsVituCustom, EmailRegex } from "shared-lib";
import { CurrentUserDto } from "@merchant_app/storage/current-user/current-user.state";
import { CurrentUserPermissions } from "@merchant_app/shared/current-user-permissions";

type FormConfig<T> = { [P in keyof T]: any[] };
type FormControls<T> = { [P in keyof T]: AbstractControl };

@Component({
    selector: "app-user-details",
    templateUrl: "./user-details.component.html",
    styleUrls: ["./user-details.component.less"],
    standalone: false
})
export class UserDetailsComponent implements OnInit, OnChanges {

    @Input() isCreate: boolean;
    @Input() user: UserDetailsDto;
    @Input() currentUser: CurrentUserDto;
    @Input() roleList: RoleDto[];
    @Input() loading: boolean;

    @Output() deleteUser = new EventEmitter<string>();
    @Output() updateUser = new EventEmitter<UserDetailsDto>();
    @Output() createUser = new EventEmitter<CreateUserDto>();
    @Output() resetUserPassword = new EventEmitter<string>();
    @Output() back = new EventEmitter<void>();

    submitButtonText: string;

    ActionButtonKind = ActionButtonKind;

    constructor(
        private fb: UntypedFormBuilder,
        private currPerms: CurrentUserPermissions
    ) {}

    private userFormConfig: FormConfig<UserDetailsDto> = {
        firstName: [null, [Validators.required]],
        lastName: [null, [Validators.required]],
        email: [null, [Validators.required, Validators.pattern(EmailRegex)]],
        roleIds: [null, [Validators.required, ValidatorsVituCustom.selectMultiIntegrity(() => this.roleList?.map((role) => role.id))]],
        isLockedOut: [false],
    };

    userForm = this.fb.group(this.userFormConfig);
    get formControls(): FormControls<UserDetailsDto> { return this.userForm.controls; }

    formControl(controlName: string) { return this.userForm.controls[controlName] as UntypedFormControl; }

    private static compareRole(a: RoleDto, b: RoleDto): number {
        const aVal = a.name?.toLowerCase();
        const bVal = b.name?.toLowerCase();
        if (aVal > bVal) {
            return 1;
        }
        else if (aVal < bVal) {
            return -1;
        }
        return 0;
    }

    ngOnInit() {

        this.submitButtonText = (this.isCreate ? "Submit" : "Update");
    }

    get pageTitle(): string {
        let firstPart: string;
        if (this.isCreate) {
            firstPart = "New";
        }
        else if(this.hasUpdateUserPermission) {
            firstPart = "Edit";
        }
        else {
            firstPart = "View";
        }
        return firstPart + " User";
    }

    get viewOnly(): boolean {
        return !this.isCreate && !this.hasUpdateUserPermission;
    }

    ngOnChanges(changes: SimpleChanges) {

        if ("user" in changes && this.user) {

            this.userForm.patchValue(this.user);

            if (!this.isCreate) {
                this.userForm.markAllAsTouched();
            }

        }

        // Workaround needed for form using ValidatorsVituCustom:
        // ValidatorsVituCustom need to be run by forcing validation if anything they depend on changes,
        // for example as a result of the allowed values being retrieved asynchronously from server (& arriving late).
        // (TODO : Consolidate use of Angular Forms in shareable form base class which implements our common form
        // behaviour like this as default for all of our forms (instead of having to apply it in multiple places))
        this.validateFormControls(this.userForm);
    }

    onClickDelete() {

        this.deleteUser.emit(this.user.id);
    }

    onClickResetPassword() {

        this.resetUserPassword.emit(this.user.id);
    }

    onClickBack() {

        this.back.emit();
    }

    onSubmit() {
        if (this.isCreate) {
            this.createUser.emit({
                ...this.user,
                ...this.userForm.value,
                resetPasswordReturnUrl: window.location.origin
            });
        }
        else {
            this.updateUser.emit({
                ...this.user,
                ...this.userForm.value,
            });
        }
    }

    get globalRoleList() {
        let retVal = [];
        if (this.roleList?.length) {
            retVal = retVal.concat(this.roleList.filter(role => !role.isCustom));
        }
        return retVal.sort(UserDetailsComponent.compareRole);
    }

    get customRoleList() {
        let retVal = [];
        if (this.roleList?.length) {
            retVal = retVal.concat(this.roleList.filter(role => role.isCustom));
        }
        return retVal.sort(UserDetailsComponent.compareRole);
    }

    get groupedRoleList() {
        return this.globalRoleList.concat(this.customRoleList);
    }

    get roleGroups() {

        const groups = [];
        let currentMinIdx = 0;

        if (this.globalRoleList?.length) {
            groups.push({name: "", minIdx: 0, maxIdx: this.globalRoleList.length - 1});
            currentMinIdx = this.globalRoleList.length;
        }
        if (this.customRoleList?.length) {
            groups.push({name: "CUSTOM", minIdx: currentMinIdx, maxIdx: currentMinIdx + this.customRoleList.length - 1});
        }

        return JSON.stringify(groups);
    }

    get hasDeleteUserPermission(): boolean {
        return this.currPerms.has(["core.users.delete"]);
    }

    get hasUpdateUserPermission(): boolean {
        return this.currPerms.has(["core.users.update"]);
    }

    get hasResetPasswordPermission(): boolean {
        return this.currPerms.has(["core.users.reset-password"]);
    }

    private validateFormControls(formGroup: UntypedFormGroup): void {
        Object.keys(formGroup?.controls).forEach(field => {
            formGroup?.get(field)?.updateValueAndValidity();
        });
    }

}
